import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Edit, Save, X, Loader, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { adminFetch } from '../../lib/adminAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = [
  { key: 'current',  label: 'Current' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past',     label: 'Past' },
];

const empty = (status) => ({
  title: '', subtitle: '', artist: '', dates: '', year: '',
  location: '', description: '', status,
  image: '', href: '', cta: '', status_label: '',
  position: 0,
});

const ExhibitionsManager = () => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('current');
  const [editing, setEditing] = useState(null); // full doc being edited (null = list)
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/exhibitions`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(`Load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((e) => e.status === status);

  const save = async () => {
    if (!editing) return;
    if (!editing.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const url = editing.id
        ? `${API_URL}/api/admin/exhibitions/${editing.id}`
        : `${API_URL}/api/admin/exhibitions`;
      const method = editing.id ? 'PUT' : 'POST';
      const { id, createdAt, updatedAt, ...body } = editing;
      const res = await adminFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success(editing.id ? 'Exhibition updated' : 'Exhibition created');
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete "${row.title}"? This cannot be undone.`)) return;
    try {
      const res = await adminFetch(`${API_URL}/api/admin/exhibitions/${row.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  // ---------- Editor form ----------
  if (editing) {
    const set = (k, v) => setEditing((prev) => ({ ...prev, [k]: v }));
    return (
      <div className="p-6 max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-light">{editing.id ? 'Edit exhibition' : 'New exhibition'}</h2>
            <p className="text-sm text-gray-500 mt-1">Fields marked * are required</p>
          </div>
          <button
            type="button"
            onClick={() => setEditing(null)}
            className="p-2 hover:bg-gray-100 rounded"
            data-testid="exhibition-editor-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <Field label="Title *"    v={editing.title}       onChange={(v) => set('title', v)}       testid="ex-title" />
          <Field label="Subtitle"   v={editing.subtitle}    onChange={(v) => set('subtitle', v)}    testid="ex-subtitle" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Artist"   v={editing.artist}      onChange={(v) => set('artist', v)}      testid="ex-artist" />
            <div>
              <label className="block text-xs uppercase tracking-[3px] text-gray-500 mb-1.5">Status</label>
              <select
                value={editing.status}
                onChange={(e) => set('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                data-testid="ex-status"
              >
                {STATUS_TABS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Dates (current/upcoming)" v={editing.dates}   onChange={(v) => set('dates', v)}   testid="ex-dates" placeholder="22 – 29 October 2026" />
            <Field label="Year (past tab)"          v={editing.year}    onChange={(v) => set('year', v)}    testid="ex-year"  placeholder="2019" />
          </div>
          <Field label="Location" v={editing.location} onChange={(v) => set('location', v)} testid="ex-location" placeholder="Zebra One Gallery · 73a Hampstead High Street, London NW3 1QX" />
          <div>
            <label className="block text-xs uppercase tracking-[3px] text-gray-500 mb-1.5">Description</label>
            <textarea
              value={editing.description}
              onChange={(e) => set('description', e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-sans"
              data-testid="ex-description"
            />
          </div>
          <Field label="Image URL (or /api/cloud-media/… path)" v={editing.image} onChange={(v) => set('image', v)} testid="ex-image" placeholder="/api/cloud-media/zebraone/exhibitions/…" />
          {editing.image && (
            <div className="border border-gray-200 rounded p-2 bg-gray-50">
              <img
                src={editing.image.startsWith('http') ? editing.image : `${API_URL}${editing.image}`}
                alt="Preview"
                className="max-h-48 mx-auto object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
          )}
          <div className="grid grid-cols-3 gap-4">
            <Field label="Status pill label" v={editing.status_label} onChange={(v) => set('status_label', v)} testid="ex-status-label" placeholder="Coming October" />
            <Field label="CTA text"          v={editing.cta}          onChange={(v) => set('cta', v)}          testid="ex-cta"          placeholder="Full exhibition details" />
            <Field label="CTA link"          v={editing.href}         onChange={(v) => set('href', v)}         testid="ex-href"         placeholder="/news?year=2026" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[3px] text-gray-500 mb-1.5">
              Position (lower = shown first within tab)
            </label>
            <input
              type="number"
              value={editing.position ?? 0}
              onChange={(e) => set('position', parseInt(e.target.value, 10) || 0)}
              className="w-32 px-3 py-2 border border-gray-300 rounded text-sm"
              data-testid="ex-position"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setEditing(null)}
            disabled={saving}
            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
            data-testid="exhibition-editor-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="px-6 py-2 text-sm bg-black text-white rounded hover:bg-gray-800 flex items-center gap-2 disabled:opacity-40"
            data-testid="exhibition-editor-save"
          >
            {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    );
  }

  // ---------- List ----------
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-light">Exhibitions</h2>
          <p className="text-sm text-gray-500 mt-1">Curate what appears on the public /exhibitions page</p>
        </div>
        <button
          type="button"
          onClick={() => setEditing(empty(status))}
          className="px-4 py-2 text-sm bg-black text-white rounded hover:bg-gray-800 flex items-center gap-2"
          data-testid="exhibition-add"
        >
          <Plus className="w-4 h-4" /> New exhibition
        </button>
      </div>

      <div className="flex items-center gap-2 border-b border-gray-200 mb-4">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setStatus(t.key)}
            className={`px-4 py-2 text-sm border-b-2 transition-colors ${
              status === t.key ? 'border-black text-black font-medium' : 'border-transparent text-gray-500 hover:text-black'
            }`}
            data-testid={`exhibition-tab-${t.key}`}
          >
            {t.label} ({items.filter((e) => e.status === t.key).length})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-400"><Loader className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-gray-500 text-sm">
          No {status} exhibitions yet. Click "New exhibition" to add one.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered
            .slice()
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .map((row) => (
            <motion.div
              key={row.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-4 p-3 border border-gray-200 rounded hover:border-black transition-colors"
              data-testid={`exhibition-row-${row.id}`}
            >
              <div className="w-20 h-16 bg-gray-100 flex items-center justify-center rounded overflow-hidden shrink-0">
                {row.image ? (
                  <img
                    src={row.image.startsWith('http') ? row.image : `${API_URL}${row.image}`}
                    alt={row.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <ImageIcon className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{row.title}</div>
                <div className="text-xs text-gray-500 truncate">
                  {row.artist}{row.artist && (row.dates || row.year) ? ' · ' : ''}{row.dates || row.year}
                  {row.status_label ? ` · ${row.status_label}` : ''}
                </div>
              </div>
              <div className="text-xs text-gray-400 shrink-0">pos {row.position ?? 0}</div>
              <button
                type="button"
                onClick={() => setEditing(row)}
                className="p-2 hover:bg-gray-100 rounded"
                title="Edit"
                data-testid={`exhibition-edit-${row.id}`}
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => del(row)}
                className="p-2 hover:bg-red-50 text-red-600 rounded"
                title="Delete"
                data-testid={`exhibition-delete-${row.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, v, onChange, testid, placeholder }) => (
  <div>
    <label className="block text-xs uppercase tracking-[3px] text-gray-500 mb-1.5">{label}</label>
    <input
      type="text"
      value={v || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
      data-testid={testid}
    />
  </div>
);

export default ExhibitionsManager;
